<template>
  <div class="file-image-input">
    <div class="fii-grid">
      <div v-for="(item, idx) in gridValues" :key="idx" class="fii-cell">
        <!-- Image -->
        <img v-if="isImageType" :src="getImgSrc(item)" class="fii-cell-img" />
        <!-- File -->
        <template v-else>
          <i :class="['fa', fileIcon(item), 'fii-cell-file-icon']" />
          <span class="fii-cell-ext">{{ fileExt(item) }}</span>
        </template>
        <button type="button" class="fii-clear" @click="removeAt(idx)">
          <i class="fa fa-times" />
        </button>
      </div>

      <label v-if="isMulti || !hasValue" :for="uid" class="fii-add">
        <i class="fa fa-plus" />
      </label>

      <input
        :id="uid"
        type="file"
        style="display: none"
        :accept="isImageType ? 'image/*' : '*/*'"
        :multiple="isMulti"
        :required="required && !gridValues.length"
        :disabled="disabled"
        @change="onChange"
      />
    </div>
  </div>
</template>

<script>
const EXT_ICONS = {
  pdf: "fa-file-pdf-o",
  doc: "fa-file-word-o",
  docx: "fa-file-word-o",
  xls: "fa-file-excel-o",
  xlsx: "fa-file-excel-o",
  ppt: "fa-file-powerpoint-o",
  pptx: "fa-file-powerpoint-o",
  zip: "fa-file-archive-o",
  rar: "fa-file-archive-o",
  mp4: "fa-file-video-o",
  mov: "fa-file-video-o",
  mp3: "fa-file-audio-o",
}

export default {
  name: "FileImageInput",
  props: {
    value: { default: null },
    type: { type: String, default: "file" },
    inputKey: { type: String, default: "" },
    title: { type: String, default: "" },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      previewUrl: null,
      previewFile: null,
      itemPreviewUrls: {},
      itemPreviewFiles: {},
    }
  },
  computed: {
    uid() {
      return "fii-" + (this.inputKey || Math.random().toString(36).slice(2))
    },
    isImageType() {
      return this.type === "image" || this.type === "images"
    },
    isMulti() {
      return this.type === "images" || this.type === "files"
    },
    hasValue() {
      return !!this.value
    },
    multiValues() {
      if (!this.value) return []
      return Array.isArray(this.value) ? this.value : [this.value]
    },
    gridValues() {
      if (this.isMulti) return this.multiValues
      return this.hasValue ? [this.value] : []
    },
    previewSrc() {
      const v = this.value
      if (!v) { this._clearPreview(); return "" }
      if (typeof v === "string") { this._clearPreview(); return v }
      if (!(v instanceof File)) { this._clearPreview(); return "" }
      if (this.previewUrl && this.previewFile === v) return this.previewUrl
      this._clearPreview()
      const url = URL.createObjectURL(v)
      this.previewUrl = url
      this.previewFile = v
      return url
    },
  },
  beforeDestroy() {
    this._clearPreview()
    Object.values(this.itemPreviewUrls).forEach((u) => u && URL.revokeObjectURL(u))
  },
  methods: {
    _clearPreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl)
        this.previewUrl = null
        this.previewFile = null
      }
    },
    getImgSrc(item) {
      if (!item) return ""
      if (typeof item === "string") return item
      if (item === this.value && !this.isMulti) return this.previewSrc
      if (!(item instanceof File)) return ""
      if (this.itemPreviewUrls[item.name] && this.itemPreviewFiles[item.name] === item) {
        return this.itemPreviewUrls[item.name]
      }
      const old = this.itemPreviewUrls[item.name]
      if (old) URL.revokeObjectURL(old)
      const url = URL.createObjectURL(item)
      this.$set(this.itemPreviewUrls, item.name, url)
      this.$set(this.itemPreviewFiles, item.name, item)
      return url
    },
    fileExt(item) {
      const name = item instanceof File ? item.name : (typeof item === "string" ? item : "")
      const parts = name.split(".")
      return parts.length > 1 ? "." + parts[parts.length - 1].toLowerCase().slice(0, 4) : "file"
    },
    fileIcon(item) {
      const name = item instanceof File ? item.name : (typeof item === "string" ? item : "")
      const ext = name.split(".").pop().toLowerCase()
      return EXT_ICONS[ext] || "fa-file-o"
    },
    onChange(event) {
      if (this.isMulti) {
        const newFiles = Array.from(event.target.files || [])
        if (!newFiles.length) return
        this.$emit("change", [...this.multiValues, ...newFiles])
      } else {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        this.$emit("change", file)
      }
      event.target.value = ""
    },
    removeAt(idx) {
      if (this.isMulti) {
        this.$emit("change", this.multiValues.filter((_, i) => i !== idx))
      } else {
        this._clearPreview()
        this.$emit("change", "")
      }
    },
  },
}
</script>

<style scoped>
.file-image-input {
  min-height: 50px;
  border: 1px solid #e2e6ea;
  border-radius: 6px;
  padding-top: 18px;
  padding-left: 12px;
  padding-bottom: 6px;
}
.fii-grid {
  display: flex;
  min-height: 35px;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;

  /* overflow: hidden; */
}
.fii-cell {
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e2e6ea;
  background: #f4f6f9;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.fii-image{
  position: relative;
}
.fii-cell-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.fii-cell-file-icon {
  font-size: 14px;
  color: #6c757d;
  line-height: 1;
}
.fii-cell-ext {
  font-size: 8px;
  color: #8a94a6;
  margin-top: 2px;
  line-height: 1;
  max-width: 38px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fii-clear {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: none;
  background: rgba(220, 53, 69, 0.85);
  color: #fff;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}
.fii-clear:hover {
  background: #c82333;
}
.fii-add {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 6px;
  border: 2px dashed #c8d0db;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #b0bac7;
  background: #f8f9fa;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  max-width: 32px;
  position: relative!important;
}
.fii-add:hover {
  border-color: #4a90e2;
  color: #4a90e2;
  background: #eef4fd;
}
</style>
